import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import dayjs from "dayjs";
import _ from "lodash";
import { ComponentLoader } from "../../../../components/loader";
import { DataTable } from "@/components/tables";
import { RectColorTag } from "@/components/tags";
import { Paginate } from "@/components/paginate";
import { getSyncHistory } from "../../../../helpers/apis";
import styles from "../../styles.module.scss";
import { SyncHistoryItem } from "../../types";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import { cn } from "@/lib/utils";

interface SyncHistoryProps {
  datastoreId: string;
  datastoreType?: string;
}

interface SyncHistoryParams {
  page: number;
  size: number;
  status?: string[];
}

const GET_SYNC_HISTORY = "GET-SYNC-HISTORY";

const SyncHistory = ({ datastoreId, datastoreType }: SyncHistoryProps): JSX.Element => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);

  // Tableau connections span multiple sites (one per data store), so each sync
  // row reports its site. Other connection types have no site concept.
  const isTableau = datastoreType === "tableau";

  const getParams = (): SyncHistoryParams => {
    const params: SyncHistoryParams = { page: page + 1, size: pageSize };
    if (statusFilter.length > 0) {
      params.status = statusFilter;
    }
    return params;
  };

  const { data: sync_data, isLoading } = useQuery({
    queryKey: [GET_SYNC_HISTORY, page, pageSize, statusFilter],
    queryFn: () => getSyncHistory(datastoreId, getParams()),
  });

  const headers: TableHeaderType<SyncHistoryItem>[] = [
    {
      id: 1,
      label: "Type",
      uid: "type",
      width: isTableau ? "25%" : "33%",
      render: (item: SyncHistoryItem) => (
        <div className={classNames(styles.type, "al-break-words")}>
          {item?.sync_event_type || ""}
        </div>
      ),
    },
    ...(isTableau
      ? [
          {
            id: 4,
            label: "Site Name",
            uid: "site_name",
            width: "25%",
            render: (item: SyncHistoryItem) => (
              <div className={classNames(styles.type, "al-break-words")}>
                {item.site_name || "-"}
              </div>
            ),
          } as TableHeaderType<SyncHistoryItem>,
        ]
      : []),
    {
      id: 2,
      label: "Time",
      uid: "time",
      width: isTableau ? "25%" : "33%",
      render: (item: SyncHistoryItem) => (
        <div className={classNames(styles.time, "al-break-words")}>
          {item.created_on ? dayjs(item.created_on).format("h:mm A, D MMMM YY") : ""}
        </div>
      ),
    },
    {
      id: 3,
      label: "Status",
      uid: "status",
      width: isTableau ? "25%" : "34%",
      render: (item: SyncHistoryItem) => (
        <div>
          {item?.sync_status && (
            <RectColorTag
              label={_.capitalize(item.sync_status)}
              color={
                item.sync_status === "running"
                  ? "purple"
                  : item.sync_status === "failed"
                    ? "pink"
                    : "lightGreen"
              }
            />
          )}
        </div>
      ),
      filter: {
        id: "status-filter",
        filterType: FilterType.DROPDOWN,
        options: [
          { label: "All", value: "" },
          { label: "Success", value: "success" },
          { label: "Running", value: "running" },
          { label: "Failed", value: "failed" },
        ],
        value: statusFilter,
        onChange: (value: string[]) => {
          setStatusFilter(value);
        },
        searchKey: "status",
      },
    },
  ];

  if (isLoading) {
    return <ComponentLoader label="Loading sync history..." />;
  }

  return (
    <div className={styles.sync_history}>
      <div className={cn("al-mb-2", styles.title)}>Sync History</div>
      <DataTable<SyncHistoryItem>
        header={headers}
        data={{ items: sync_data?.items || [] }}
        idKey={item => `${item.sync_event_type}-${item.created_on}`}
      />
      <div className={styles.paginate}>
        <Paginate
          itemCount={sync_data?.total || 0}
          page={page}
          pageSize={pageSize}
          numPages={sync_data?.pages || 1}
          onPageClick={setPage}
          onPageSizeChange={setPageSize}
          showPageSize={true}
        />
      </div>
    </div>
  );
};

export default SyncHistory;
